// The company's term ("AI" or "UI", profile field ai_term) for every UI text, in one place: the
// strings object in sl.ts is read through a Proxy that writes the current term into each string,
// array item and text-function result at the moment it is read. With "AI" every value comes back
// exactly as written in sl.ts.
//
// Values passed to text functions (tool and company names, user data, numbers) never change: their
// "AI" is protected before the call (term.ts protect) and restored after the transform.
// The current term is set by TermRoot (lib/term.tsx), which remounts the pages when it changes.

import { PRODUCT_NAMES, protect, protectedNames, termText, type AiTerm } from "./term";

/** Product names in UI texts stay as they are. */
const NAMES = protectedNames(PRODUCT_NAMES);

let current: AiTerm = "AI";
const listeners = new Set<(term: AiTerm) => void>();

export function currentTerm(): AiTerm {
  return current;
}

/** Sets the term for everything rendered from now on; listeners hear about a change. */
export function setCurrentTerm(term: AiTerm): void {
  if (term === current) return;
  current = term;
  for (const fn of [...listeners]) fn(term);
}

/** Called after the term changes (caches of server texts drop their copy). */
export function onTermChange(fn: (term: AiTerm) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

type Fn = (...args: unknown[]) => unknown;

/** An argument of a text function: its "AI" kept (strings, also inside lists). */
function protectArg(arg: unknown): unknown {
  if (typeof arg === "string") return protect(arg);
  if (Array.isArray(arg)) return arg.map(protectArg);
  return arg;
}

/** A text function's result in the term (strings, also inside lists). */
function termResult(value: unknown, term: AiTerm): unknown {
  if (typeof value === "string") return termText(value, term, NAMES);
  if (Array.isArray(value)) return value.map((v) => termResult(v, term));
  return value;
}

/**
 * The strings object seen through the term. `getTerm` is read on every access (tests pass their
 * own); nested objects and functions are wrapped once and cached, so identities stay stable.
 */
export function withTerm<T extends object>(strings: T, getTerm: () => AiTerm = currentTerm): T {
  const objects = new WeakMap<object, object>();
  const functions = new WeakMap<Fn, Fn>();

  const wrapFn = (fn: Fn): Fn => {
    let w = functions.get(fn);
    if (!w) {
      w = function (this: unknown, ...args: unknown[]) {
        const term = getTerm();
        if (term === "AI") return fn.apply(this, args);
        return termResult(fn.apply(this, args.map(protectArg)), term);
      };
      functions.set(fn, w);
    }
    return w;
  };

  const wrap = (value: unknown): unknown => {
    if (typeof value === "string") {
      const term = getTerm();
      return term === "AI" ? value : termText(value, term, NAMES);
    }
    if (typeof value === "function") return wrapFn(value as Fn);
    if (value !== null && typeof value === "object") return wrapObj(value);
    return value;
  };

  const wrapObj = (obj: object): object => {
    let p = objects.get(obj);
    if (!p) {
      p = new Proxy(obj, {
        get(target, key, receiver) {
          const v = Reflect.get(target, key, receiver);
          // only the object's own texts: array methods, length and symbols as they are
          if (typeof key === "symbol" || !Object.prototype.hasOwnProperty.call(target, key)) return v;
          return wrap(v);
        },
      });
      objects.set(obj, p);
    }
    return p;
  };

  return wrapObj(strings) as T;
}
